import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = params.get("token");
    if (t) setToken(t);
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd !== pwd2) {
      setErr("Lozinke se ne podudaraju");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/prijava"), 2200);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 circuit-bg opacity-25" />
      <div className="absolute inset-0 bg-black/85" />
      <div className="absolute inset-0 nasa-grid-bg opacity-50" />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Logo size={70} />
          <h1 className="mt-5 font-display text-2xl font-bold tracking-wider">
            <span className="text-neon-green">Nova</span>{" "}
            <span className="text-neon-orange">lozinka</span>
          </h1>
        </div>

        <form onSubmit={onSubmit} className="nasa-card p-8 space-y-5" data-testid="reset-form">
          {done ? (
            <div className="flex flex-col items-center text-center py-6 space-y-3">
              <CheckCircle2 className="w-12 h-12 text-[#22ff77]" />
              <div className="font-display text-lg">Lozinka uspešno promenjena</div>
              <div className="text-xs text-zinc-500">Preusmeravanje na prijavu...</div>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-400">Postavite novu lozinku za vaš nalog.</p>

              <div className="space-y-1">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">Token</label>
                <input
                  data-testid="reset-token"
                  required
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="w-full bg-black border border-white/10 px-3 py-3 text-xs font-mono focus:border-[#22ff77] focus:outline-none rounded-sm"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">Nova lozinka</label>
                <input
                  data-testid="reset-password"
                  type="password"
                  required
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full bg-black border border-white/10 px-3 py-3 text-sm focus:border-[#22ff77] focus:outline-none rounded-sm"
                  placeholder="min. 6 karaktera"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">Potvrdi lozinku</label>
                <input
                  data-testid="reset-password-confirm"
                  type="password"
                  required
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="w-full bg-black border border-white/10 px-3 py-3 text-sm focus:border-[#22ff77] focus:outline-none rounded-sm"
                />
              </div>

              {err && (
                <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2 rounded-sm">
                  {err}
                </div>
              )}

              <button
                data-testid="reset-submit"
                disabled={loading}
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green disabled:opacity-60"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Sačuvaj lozinku
              </button>

              <div className="text-center text-xs text-zinc-500 pt-2 border-t border-white/5">
                <Link to="/prijava" className="text-[#ff7a18] hover:underline">Nazad na prijavu</Link>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
